/**
 * VÉRAULT Portal — Client · Connect (Wave 30.2 · Phase 8c)
 * ────────────────────────────────────────────────────────────────
 * Two calm choices, framed editorially:
 *   1. Schedule a Call  → Nylas embed (existing flow, preserved)
 *   2. New Travel Request → calm form (occasion, party, window, vision,
 *      preferred channel) → POST to /client-portal/travel-requests
 *      → creates BOTH a concierge conversation + a tracking row.
 *
 * Mobile-first. NO support-ticket aesthetic. Reads like writing a
 * quiet letter to your advisor about a new chapter.
 */
import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  CalendarRange, MailPlus, ChevronLeft, Loader2, Check,
  Sparkles, Phone, CalendarCheck2,
} from "lucide-react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import {
  Surface, PageHeader, EditorialRule, PortalButton,
  api, motion as portalMotion,
} from "../../index";
import NylasScheduler from "../../../components/public/NylasScheduler";

const { fadeInUp, stagger } = portalMotion;

const NYLAS_CONFIGURATION_ID =
  process.env.REACT_APP_NYLAS_CONFIGURATION_ID || "aafea4b5-1887-4198-899f-dce1d9edcffd";
const NYLAS_CLIENT_ID =
  process.env.REACT_APP_NYLAS_CLIENT_ID || "ae93a2f7-6506-480c-916a-bd3b914ff84a";
const NYLAS_SCHEDULER_SLUG =
  process.env.REACT_APP_NYLAS_SCHEDULER_SLUG || "verault-travel-45min";
const NYLAS_FALLBACK_URL = `https://book.nylas.com/us/${NYLAS_CLIENT_ID}/${NYLAS_SCHEDULER_SLUG}`;

// ─── Mode selector ──────────────────────────────────────────────────
function ModeSelector({ onPick }) {
  return (
    <motion.div variants={fadeInUp} className="grid gap-5 md:grid-cols-2" data-testid="connect-mode-selector">
      <button
        type="button"
        onClick={() => onPick("schedule")}
        data-testid="connect-mode-schedule"
        className="group relative text-left bg-[hsl(var(--background))] border border-[hsl(var(--border))] hover:border-[hsl(var(--accent-gold))]/55 rounded-lg p-7 sm:p-9 transition-all hover:shadow-floating"
      >
        <div className="inline-flex items-center justify-center size-12 rounded-full bg-[hsl(var(--background-paper))] text-[hsl(var(--accent-gold))] mb-5 group-hover:bg-[hsl(var(--accent-gold))]/12 transition-colors">
          <CalendarRange className="size-5" strokeWidth={1.25} />
        </div>
        <p className="font-mono text-[10px] uppercase tracking-[0.28em] text-[hsl(var(--foreground-muted))]">
          Quiet conversation
        </p>
        <h3 className="mt-2 font-serif text-[22px] text-[hsl(var(--foreground))]">
          Schedule a call.
        </h3>
        <p className="mt-3 text-[14px] leading-relaxed text-[hsl(var(--foreground-secondary))] font-serif">
          Forty-five minutes with your advisor. Anything on your mind — a shift in dates, a fresh question, or a moment to dream a little.
        </p>
      </button>

      <button
        type="button"
        onClick={() => onPick("request")}
        data-testid="connect-mode-request"
        className="group relative text-left bg-[hsl(var(--background))] border border-[hsl(var(--border))] hover:border-[hsl(var(--accent-gold))]/55 rounded-lg p-7 sm:p-9 transition-all hover:shadow-floating"
      >
        <div className="inline-flex items-center justify-center size-12 rounded-full bg-[hsl(var(--background-paper))] text-[hsl(var(--accent-gold))] mb-5 group-hover:bg-[hsl(var(--accent-gold))]/12 transition-colors">
          <MailPlus className="size-5" strokeWidth={1.25} />
        </div>
        <p className="font-mono text-[10px] uppercase tracking-[0.28em] text-[hsl(var(--foreground-muted))]">
          A new chapter
        </p>
        <h3 className="mt-2 font-serif text-[22px] text-[hsl(var(--foreground))]">
          New travel request.
        </h3>
        <p className="mt-3 text-[14px] leading-relaxed text-[hsl(var(--foreground-secondary))] font-serif">
          An anniversary, a repeat, an exploratory whisper — share the shape of what you're imagining and your advisor will respond with care.
        </p>
      </button>
    </motion.div>
  );
}

// ─── Schedule mode ──────────────────────────────────────────────────
function ScheduleMode({ onBack, profile }) {
  const [params, setParams] = useSearchParams();
  const [booked, setBooked] = useState(params.get("booked") === "1");

  const bookingConfirmationRedirectUrl =
    typeof window !== "undefined"
      ? `${window.location.origin}/portal/client/connect?mode=schedule&booked=1`
      : undefined;

  const handleBooking = () => {
    setBooked(true);
    setParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set("booked", "1");
      return next;
    }, { replace: true });
  };

  if (booked) {
    return (
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-6">
        <button
          type="button"
          onClick={() => { setBooked(false); onBack(); }}
          className="inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--foreground-muted))] hover:text-[hsl(var(--foreground))]"
          data-testid="schedule-back"
        >
          <ChevronLeft className="size-3.5" strokeWidth={1.5} /> Back to connect
        </button>
        <Surface variant="default" padded className="!p-10 text-center" data-testid="schedule-confirmed">
          <div className="inline-flex items-center justify-center size-14 rounded-full bg-[hsl(var(--accent-gold))]/15 text-[hsl(var(--accent-gold))] mb-5">
            <CalendarCheck2 className="size-7" strokeWidth={1.25} />
          </div>
          <h3 className="font-serif text-[24px] text-[hsl(var(--foreground))]">
            Your time is held.
          </h3>
          <p className="mt-2 font-serif italic text-[15px] text-[hsl(var(--foreground-secondary))] max-w-md mx-auto">
            We've sent a calendar invitation and a quiet confirmation by email.
          </p>
        </Surface>
      </motion.div>
    );
  }

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-6">
      <button
        type="button"
        onClick={onBack}
        className="inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--foreground-muted))] hover:text-[hsl(var(--foreground))]"
        data-testid="schedule-back"
      >
        <ChevronLeft className="size-3.5" strokeWidth={1.5} /> Back to connect
      </button>
      <EditorialRule label="Choose a time" />
      {/* Wave 30.4.5 — Editorial frame around the Nylas embed (matches Schedule.jsx) */}
      <div
        className="mt-6 relative bg-[hsl(var(--background))] dark:bg-[hsl(var(--background-paper))] rounded-md overflow-hidden"
        data-testid="client-nylas-frame"
      >
        <div className="h-px bg-gradient-to-r from-transparent via-[hsl(var(--accent-gold))]/35 to-transparent" />
        <div className="px-2 sm:px-4 py-3">
          <p className="text-center font-accent italic text-[12.5px] text-[hsl(var(--foreground-muted))]">
            Times are shown in your local timezone — your advisor will adapt to it.
          </p>
        </div>
        <div data-testid="client-nylas-embed">
          <NylasScheduler
            configurationId={NYLAS_CONFIGURATION_ID}
            clientId={NYLAS_CLIENT_ID}
            bookingConfirmationRedirectUrl={bookingConfirmationRedirectUrl}
            fallbackUrl={NYLAS_FALLBACK_URL}
            prefilledName={profile?.full_name || profile?.first_name || ""}
            prefilledEmail={profile?.email || ""}
            testid="client-nylas"
            onBooking={handleBooking}
          />
        </div>
        <div className="h-px bg-gradient-to-r from-transparent via-[hsl(var(--accent-gold))]/35 to-transparent" />
      </div>
      <p className="text-center font-mono text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--foreground-muted))]">
        <Phone className="inline size-3 mr-1 -mt-0.5" strokeWidth={1.5} />
        Prefer email?{" "}
        <a
          href={NYLAS_FALLBACK_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="underline underline-offset-4 hover:text-[hsl(var(--foreground))]"
        >
          Open in a new tab
        </a>
      </p>
    </motion.div>
  );
}

// ─── Travel request mode ────────────────────────────────────────────
function TravelRequestMode({ onBack }) {
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null);
  const [form, setForm] = useState({
    occasion: "",
    traveler_count: "",
    window_text: "",
    vision: "",
    preferred_channel: "",
    preferred_call_time: "",
  });
  const [error, setError] = useState("");

  const setField = (k) => (e) => {
    const v = e?.target?.value ?? e;
    setForm((f) => ({ ...f, [k]: v }));
  };

  const canSubmit = form.vision.trim().length >= 8 && !submitting;

  const onSubmit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    setError("");
    try {
      const payload = {
        occasion: form.occasion.trim() || null,
        traveler_count: form.traveler_count
          ? Number(form.traveler_count) || null
          : null,
        window_text: form.window_text.trim() || null,
        vision: form.vision.trim(),
        preferred_channel: form.preferred_channel || null,
        preferred_call_time: form.preferred_call_time.trim() || null,
      };
      const { data } = await api.post("/client-portal/travel-requests", payload);
      setDone(data);
    } catch (e) {
      setError(
        e?.response?.data?.detail ||
        "Something kept your note from sending. Please try again in a moment.",
      );
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-6">
        <Surface variant="default" padded className="!p-10 text-center" data-testid="travel-request-confirmed">
          <div className="inline-flex items-center justify-center size-14 rounded-full bg-[hsl(var(--accent-gold))]/15 text-[hsl(var(--accent-gold))] mb-5">
            <Sparkles className="size-7" strokeWidth={1.25} />
          </div>
          <h3 className="font-serif text-[24px] text-[hsl(var(--foreground))]">
            Your note has been sent.
          </h3>
          <p className="mt-2 font-serif italic text-[15px] text-[hsl(var(--foreground-secondary))] max-w-md mx-auto">
            Your advisor will read this carefully and respond with the next quiet steps.
          </p>
          <div className="mt-7 flex items-center justify-center gap-2">
            <PortalButton
              as={Link}
              to={`/portal/client/messages?thread=${done.conversation_id}`}
              variant="primary"
              size="sm"
              icon={<Check className="size-3.5" strokeWidth={1.5} />}
              data-testid="travel-request-view-thread"
            >
              Open the thread
            </PortalButton>
            <PortalButton
              variant="ghost"
              size="sm"
              onClick={() => navigate("/portal/client")}
              data-testid="travel-request-back-home"
            >
              Back to your suite
            </PortalButton>
          </div>
        </Surface>
      </motion.div>
    );
  }

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-6">
      <button
        type="button"
        onClick={onBack}
        className="inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--foreground-muted))] hover:text-[hsl(var(--foreground))]"
        data-testid="request-back"
      >
        <ChevronLeft className="size-3.5" strokeWidth={1.5} /> Back to connect
      </button>

      <Surface variant="default" padded className="!p-7 sm:!p-9" data-testid="travel-request-form">
        <div className="space-y-7">
          <Field
            label="Occasion"
            hint="Anniversary · honeymoon · family reunion · a quiet pause"
            id="tr-occasion"
          >
            <input
              id="tr-occasion"
              type="text"
              value={form.occasion}
              onChange={setField("occasion")}
              placeholder="What kind of moment?"
              className="vt-input"
              data-testid="tr-occasion"
              maxLength={120}
            />
          </Field>

          <div className="grid sm:grid-cols-2 gap-5">
            <Field label="Party" hint="How many traveling?" id="tr-count">
              <input
                id="tr-count"
                type="number"
                min="1"
                max="20"
                value={form.traveler_count}
                onChange={setField("traveler_count")}
                placeholder="2"
                className="vt-input"
                data-testid="tr-count"
              />
            </Field>
            <Field label="Window" hint="Late September 2026 · Q1 2027 · flexible" id="tr-window">
              <input
                id="tr-window"
                type="text"
                value={form.window_text}
                onChange={setField("window_text")}
                placeholder="When, roughly?"
                className="vt-input"
                data-testid="tr-window"
                maxLength={120}
              />
            </Field>
          </div>

          <Field
            label="The vision"
            hint="Share the shape of what you're imagining — destinations, feelings, pace, anything at all."
            id="tr-vision"
          >
            <textarea
              id="tr-vision"
              value={form.vision}
              onChange={setField("vision")}
              rows={6}
              placeholder="A quiet beach week somewhere warm. We want time to breathe, ocean nearby, no agenda…"
              className="vt-input vt-textarea font-serif"
              data-testid="tr-vision"
              maxLength={2000}
            />
          </Field>

          <div className="grid sm:grid-cols-2 gap-5">
            <Field label="Preferred contact" hint="How would you like to hear back?" id="tr-channel">
              <select
                id="tr-channel"
                value={form.preferred_channel}
                onChange={setField("preferred_channel")}
                className="vt-input"
                data-testid="tr-channel"
              >
                <option value="">No preference</option>
                <option value="email">Email</option>
                <option value="phone">Phone</option>
                <option value="video">Video call</option>
              </select>
            </Field>
            <Field label="Ideal call time" hint="Weekday mornings, evenings — whatever suits" id="tr-call-time">
              <input
                id="tr-call-time"
                type="text"
                value={form.preferred_call_time}
                onChange={setField("preferred_call_time")}
                placeholder="Weekday mornings"
                className="vt-input"
                data-testid="tr-call-time"
                maxLength={120}
              />
            </Field>
          </div>

          {error ? (
            <p className="text-[13px] text-[hsl(var(--accent-terracotta))]" data-testid="tr-error">{error}</p>
          ) : null}

          <div className="flex items-center justify-end gap-2 pt-2">
            <PortalButton variant="ghost" size="sm" onClick={onBack}>
              Cancel
            </PortalButton>
            <PortalButton
              variant="primary"
              size="sm"
              onClick={onSubmit}
              disabled={!canSubmit}
              icon={submitting ? <Loader2 className="size-3.5 animate-spin" /> : <MailPlus className="size-3.5" />}
              data-testid="tr-submit"
            >
              Send to your advisor
            </PortalButton>
          </div>
        </div>
      </Surface>

      <style>{`
        .vt-input {
          width: 100%;
          background-color: hsl(var(--background));
          border: 1px solid hsl(var(--border));
          border-radius: 6px;
          padding: 10px 14px;
          font-size: 15px;
          color: hsl(var(--foreground));
          outline: none;
          transition: border-color 0.2s ease;
        }
        .vt-input:focus { border-color: hsl(var(--accent-gold) / 0.6); }
        .vt-textarea { resize: vertical; line-height: 1.6; }
      `}</style>
    </motion.div>
  );
}

function Field({ label, hint, id, children }) {
  return (
    <div>
      <label htmlFor={id} className="block font-mono text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--foreground))]">
        {label}
      </label>
      {hint ? (
        <p className="mt-0.5 mb-2 text-[12px] font-serif italic text-[hsl(var(--foreground-muted))]">{hint}</p>
      ) : (
        <div className="mb-2" />
      )}
      {children}
    </div>
  );
}

// ─── Page ──────────────────────────────────────────────────────────
export default function ClientConnectPage() {
  const [params, setParams] = useSearchParams();
  const [mode, setMode] = useState(params.get("mode") || null);
  const [profile, setProfile] = React.useState(null);

  React.useEffect(() => {
    let alive = true;
    api.get("/client-portal/profile")
      .then(({ data }) => alive && setProfile(data))
      .catch(() => alive && setProfile({}));
    return () => { alive = false; };
  }, []);

  const setModeAndUrl = (next) => {
    setMode(next);
    setParams((prev) => {
      const np = new URLSearchParams(prev);
      if (next) np.set("mode", next);
      else np.delete("mode");
      return np;
    }, { replace: true });
  };

  return (
    <motion.div initial="initial" animate="animate" variants={stagger} className="space-y-8" data-testid="client-connect-page">
      <motion.div variants={fadeInUp}>
        <PageHeader
          eyebrow="Connect"
          title="How can we serve you?"
          lede="A quiet line for a conversation, or a fresh chapter just beginning to take shape."
        />
      </motion.div>

      <AnimatePresence mode="wait">
        {mode === "schedule" ? (
          <ScheduleMode key="schedule" onBack={() => setModeAndUrl(null)} profile={profile} />
        ) : mode === "request" ? (
          <TravelRequestMode key="request" onBack={() => setModeAndUrl(null)} />
        ) : (
          <ModeSelector key="picker" onPick={setModeAndUrl} />
        )}
      </AnimatePresence>
    </motion.div>
  );
}
